/**
 * CEFR level estimation.
 *
 * Every competitor in the set shows a vanity percentage; we publish an estimate with a
 * confidence band and the evidence behind it (docs/04-screen-specs.md F2). That is only
 * defensible if the estimate is actually derived from measurements, which is what this
 * module does — deterministically, from five weighted inputs
 * (docs/05-content-pedagogy.md §2).
 */

import {
  CEFR_SUBLEVELS,
  type CefrSublevel,
  cefrFromIndex,
  cefrIndex,
  clamp,
  round,
} from '../types.js';

export interface ProductiveComplexity {
  /** Mean words per clause across recent scored utterances. */
  readonly meanClauseLength: number;
  /** Fraction of utterances containing a subordinate clause, 0–1. */
  readonly subordinationRate: number;
  /** Distinct tenses/moods used, out of the ~8 that matter below C1. */
  readonly tenseVariety: number;
  /** Type-token ratio over recent utterances, 0–1. */
  readonly typeTokenRatio: number;
  /** Number of utterances the above are computed from. */
  readonly sampleSize: number;
}

export interface EstimateInput {
  /** Mastered items weighted by frequency band → effective active vocabulary. */
  readonly vocabEstimate: number;
  readonly productive: ProductiveComplexity;
  /** Mean mastery across the CEFR grammar concept map, 0–1. */
  readonly grammarMastery: number;
  /**
   * Comprehension accuracy at known difficulty, 0–1, or null when never measured.
   *
   * Null and zero are different claims and used to be the same number. The API passed a
   * literal `0` — no listening signal was computed anywhere — and listening carries 15%
   * of the weight here, so every learner was scored as though they had got every
   * listening question wrong. An unmeasured skill now carries no weight instead of a
   * failing score; see `estimate`.
   */
  readonly listeningAccuracy: number | null;
  /** Dialect-normalized intelligibility from pronunciation scores, 0–100. */
  readonly intelligibility: number;
  /** Previous estimate, used to damp movement. Null on first placement. */
  readonly previous: { level: CefrSublevel; confidence: number } | null;
  /** How many days since the last estimate. Evidence decays. */
  readonly daysSincePrevious: number;
}

export interface CefrEstimate {
  readonly level: CefrSublevel;
  /** 0–1. Shown to the learner as a band, never hidden. */
  readonly confidence: number;
  /** Half-width of the band in sub-levels, e.g. 1 → "±1 sub-level". */
  readonly bandSublevels: number;
  readonly evidence: EstimateEvidence;
}

export interface EstimateEvidence {
  readonly components: Readonly<Record<EstimateComponent, { score: number; weight: number }>>;
  readonly weightedIndex: number;
  readonly sampleSize: number;
  readonly damped: boolean;
}

export type EstimateComponent =
  | 'productive'
  | 'vocabulary'
  | 'grammar'
  | 'listening'
  | 'pronunciation';

const WEIGHTS: Readonly<Record<EstimateComponent, number>> = {
  productive: 0.3,
  vocabulary: 0.25,
  grammar: 0.2,
  listening: 0.15,
  pronunciation: 0.1,
};

const MAX_INDEX = CEFR_SUBLEVELS.length - 1;

/**
 * Vocabulary-size boundaries per sub-level, from the active-vocabulary anchors in
 * docs/05-content-pedagogy.md §2. Interpolated between points.
 */
const VOCAB_ANCHORS: readonly (readonly [vocab: number, index: number])[] = [
  [0, 0],
  [300, 0],
  [600, 1],
  [900, 2],
  [1200, 3],
  [1500, 4],
  [2000, 5],
  [2500, 6],
  [3000, 7],
  [4000, 8],
  [5000, 9],
  [6000, 10],
  [8000, 11],
  [10000, 12],
];

/** Maps an active-vocabulary estimate onto the sub-level scale. */
export function vocabToIndex(vocab: number): number {
  const v = Math.max(0, vocab);
  for (let i = 1; i < VOCAB_ANCHORS.length; i++) {
    const [hiV, hiI] = VOCAB_ANCHORS[i] as readonly [number, number];
    if (v <= hiV) {
      const [loV, loI] = VOCAB_ANCHORS[i - 1] as readonly [number, number];
      const span = hiV - loV;
      const t = span === 0 ? 0 : (v - loV) / span;
      return loI + t * (hiI - loI);
    }
  }
  return MAX_INDEX;
}

/**
 * Productive complexity → sub-level index. Clause length and subordination are the two
 * strongest discriminators between A2, B1 and B2 in the literature, so they carry most
 * of the weight here.
 */
export function productiveToIndex(p: ProductiveComplexity): number {
  const clause = clamp((p.meanClauseLength - 3) / 9, 0, 1); // 3 words → 0, 12 words → 1
  const subordination = clamp(p.subordinationRate / 0.6, 0, 1); // 60% → ceiling
  const tense = clamp(p.tenseVariety / 8, 0, 1);
  const lexical = clamp((p.typeTokenRatio - 0.25) / 0.35, 0, 1);

  const composite = clause * 0.4 + subordination * 0.3 + tense * 0.2 + lexical * 0.1;
  return composite * MAX_INDEX;
}

export function estimate(input: EstimateInput): CefrEstimate {
  const productiveIdx = productiveToIndex(input.productive);
  const vocabIdx = vocabToIndex(input.vocabEstimate);
  const grammarIdx = clamp(input.grammarMastery, 0, 1) * MAX_INDEX;
  const listeningIdx =
    input.listeningAccuracy === null ? 0 : clamp(input.listeningAccuracy, 0, 1) * MAX_INDEX;
  // Intelligibility saturates: being perfectly intelligible does not make you C1, so it
  // is capped at the B1/B2 boundary as a *supporting* signal only.
  const pronunciationIdx = clamp(input.intelligibility / 100, 0, 1) * (MAX_INDEX * 0.7);

  /*
   * An unmeasured component carries no weight, and the rest are renormalised.
   *
   * Weighting a skill nobody has tested at zero is not a conservative estimate, it is a
   * wrong one: it asserts the learner failed something they were never asked. Listening
   * is 15% of this model and the API passed a hardcoded `0` for it, so every level this
   * product has ever reported was pulled down by up to 15% of the whole scale — and
   * `confidenceFor` withheld its listening breadth as well, on the same missing number.
   *
   * Renormalising means the remaining evidence decides the level and the missing
   * evidence shows up where it belongs: in the confidence, and in a component whose
   * weight is visibly zero.
   */
  const measuredWeights: Record<EstimateComponent, number> = {
    ...WEIGHTS,
    listening: input.listeningAccuracy === null ? 0 : WEIGHTS.listening,
  };
  const totalWeight = Object.values(measuredWeights).reduce((sum, weight) => sum + weight, 0);
  const normalise = (weight: number) => (totalWeight === 0 ? 0 : weight / totalWeight);

  const components = {
    productive: { score: productiveIdx, weight: normalise(measuredWeights.productive) },
    vocabulary: { score: vocabIdx, weight: normalise(measuredWeights.vocabulary) },
    grammar: { score: grammarIdx, weight: normalise(measuredWeights.grammar) },
    listening: { score: listeningIdx, weight: normalise(measuredWeights.listening) },
    pronunciation: { score: pronunciationIdx, weight: normalise(measuredWeights.pronunciation) },
  } as const;

  let weightedIndex = 0;
  for (const key of Object.keys(components) as EstimateComponent[]) {
    const c = components[key];
    weightedIndex += c.score * c.weight;
  }

  const confidence = confidenceFor(input);

  // Damping: a single good session must not move a learner a whole band. We move at
  // most one sub-level per estimate unless confidence in the new reading is high.
  let damped = false;
  if (input.previous !== null) {
    const prevIdx = cefrIndex(input.previous.level);
    const maxStep = confidence >= 0.75 ? 2 : 1;
    const delta = weightedIndex - prevIdx;
    if (Math.abs(delta) > maxStep) {
      weightedIndex = prevIdx + Math.sign(delta) * maxStep;
      damped = true;
    }
  }

  const level = cefrFromIndex(weightedIndex);

  return {
    level,
    confidence: round(confidence, 2),
    bandSublevels: bandWidth(confidence),
    evidence: {
      components,
      weightedIndex: round(weightedIndex, 2),
      sampleSize: input.productive.sampleSize,
      damped,
    },
  };
}

/**
 * Confidence grows with evidence volume and shrinks with staleness. It is deliberately
 * conservative at low sample sizes: a placement test of three utterances should not
 * produce a confident claim, and saying so is the honest thing.
 */
export function confidenceFor(input: EstimateInput): number {
  const n = Math.max(0, input.productive.sampleSize);
  // Saturating curve: 10 utterances ≈ 0.45, 50 ≈ 0.75, 200 ≈ 0.9.
  const volume = n / (n + 22);

  const breadth =
    (input.grammarMastery > 0 ? 1 : 0) * 0.1 +
    // Measured, not non-zero: a learner who genuinely got every listening question
    // wrong has still given us listening evidence, and used to be treated as having
    // given none — indistinguishable from the null this field carried for everyone.
    (input.listeningAccuracy !== null ? 1 : 0) * 0.1 +
    (input.vocabEstimate > 0 ? 1 : 0) * 0.1;

  const staleness = clamp(1 - input.daysSincePrevious / 120, 0.5, 1);
  const carried = input.previous === null ? 0 : input.previous.confidence * 0.2 * staleness;

  return clamp(volume * 0.75 + breadth + carried, 0.15, 0.95);
}

/** Half-width of the shown confidence band, in sub-levels. */
export function bandWidth(confidence: number): number {
  if (confidence >= 0.8) return 0;
  if (confidence >= 0.6) return 1;
  return 2;
}

/**
 * Target difficulty for content selection: one sub-level above current, which is the
 * i+1 principle made operational (docs/05-content-pedagogy.md §1).
 */
export function targetContentLevel(current: CefrSublevel): CefrSublevel {
  return cefrFromIndex(cefrIndex(current) + 1);
}

/** Human-readable band for the UI, e.g. "A2 · Elementary · ±1 sub-level". */
export const BAND_LABELS: Readonly<Record<string, string>> = {
  A1: 'Beginner',
  A2: 'Elementary',
  B1: 'Intermediate',
  B2: 'Upper intermediate',
  C1: 'Advanced',
};
